import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];
  #mediaQuery;
  #observer;
  #events;
  #visible = false;
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute('data-desktop-video')) {
      this.#loadSources();
      return;
    }

    this.#mediaQuery = window.matchMedia('(min-width: 750px) and (prefers-reduced-motion: no-preference)');
    this.#events = new AbortController();
    this.#mediaQuery.addEventListener('change', this.#updatePlayback, { signal: this.#events.signal });
    this.#observer = new IntersectionObserver(
      (entries) => {
        this.#visible = entries.some((entry) => entry.isIntersecting);
        this.#updatePlayback();
      },
      { rootMargin: '200px 0px' }
    );
    this.#observer.observe(this);
    this.#updatePlayback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#events?.abort();
    this.#observer?.disconnect();
    if (this.hasAttribute('data-desktop-video')) this.#unloadSources();
    this.#loaded = false;
    this.#visible = false;
  }

  #loadSources() {
    if (this.#loaded) return;
    const { videoSources, videoElement } = this.refs;
    for (const source of videoSources) {
      if (source.dataset.videoSource) source.setAttribute('src', source.dataset.videoSource);
    }
    videoElement.load();
    this.#loaded = true;
  }

  #unloadSources() {
    const { videoSources, videoElement } = this.refs;
    videoElement.pause();
    if (!this.#loaded) return;
    for (const source of videoSources) source.removeAttribute('src');
    videoElement.removeAttribute('src');
    videoElement.load();
    this.#loaded = false;
  }

  #updatePlayback = () => {
    if (!this.#mediaQuery.matches) {
      this.#unloadSources();
      return;
    }
    if (!this.#visible) {
      this.refs.videoElement.pause();
      return;
    }
    this.#loadSources();
    this.refs.videoElement.play()?.catch(() => {});
  };
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
